import { tokens as t } from "./tokens"

const baseButton = {
  minHeight: t.controlHeight.md,
  padding: "0 14px",
  borderRadius: t.radius.sm,
  fontSize: 12,
  fontWeight: 800,
  cursor: "pointer",
  whiteSpace: "nowrap",
  transition: "all 0.18s ease",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8
}

export const ui = {
  page: {
    minHeight: "100%",
    background: t.color.appBg
  },

  pageContent: {
    width: "100%",
    maxWidth: t.layout.contentMaxWidth,
    margin: "0 auto"
  },

  topbarShell: {
    minHeight: t.layout.topbarHeight,
    padding: "14px 20px",
    borderBottom: `1px solid ${t.color.border}`,
    background: "rgba(255,255,255,0.88)",
    backdropFilter: "blur(12px)",
    position: "sticky",
    top: 0,
    zIndex: 40
  },

  topbarTitleStack: {
    minWidth: 0,
    display: "grid",
    gap: 4
  },

  topbarEyebrow: {
    fontSize: t.type.overline.fontSize,
    fontWeight: t.type.overline.fontWeight,
    lineHeight: t.type.overline.lineHeight,
    color: t.color.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.08em"
  },

  topbarTitle: {
    fontSize: 22,
    fontWeight: 900,
    lineHeight: 1.05,
    color: t.color.text,
    letterSpacing: "-0.03em"
  },

  topbarSubtitle: {
    fontSize: 12,
    color: t.color.textMuted,
    lineHeight: 1.4
  },

  topbarControls: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    justifyContent: "flex-end"
  },

  topbarPanel: {
    minHeight: 52,
    padding: "8px 12px",
    borderRadius: 14,
    border: `1px solid ${t.color.border}`,
    background: "rgba(255,255,255,0.94)",
    boxShadow: t.shadow.subtle,
    display: "flex",
    alignItems: "center",
    gap: 10
  },

  pageHeader: {
    marginBottom: t.spacing.lg
  },

  pageTitle: {
    fontSize: t.type.page.fontSize,
    fontWeight: t.type.page.fontWeight,
    lineHeight: t.type.page.lineHeight,
    color: t.color.text,
    letterSpacing: "-0.02em"
  },

  pageSubtitle: {
    fontSize: t.type.body.fontSize,
    fontWeight: 500,
    lineHeight: t.type.body.lineHeight,
    color: t.color.textMuted,
    marginTop: 4
  },

  heroCard: {
    background: t.gradient.hero,
    color: "#FFFFFF",
    borderRadius: t.radius.lg,
    boxShadow: t.shadow.hero,
    padding: 18,
    position: "relative",
    overflow: "hidden"
  },

  heroTitle: {
    fontSize: t.type.hero.fontSize,
    fontWeight: t.type.hero.fontWeight,
    lineHeight: t.type.hero.lineHeight,
    letterSpacing: "-0.03em"
  },

  heroSubtitle: {
    fontSize: 13,
    fontWeight: 500,
    color: "rgba(255,255,255,0.82)",
    lineHeight: 1.45,
    marginTop: 6
  },

  card: {
    background: t.color.surface,
    border: `1px solid ${t.color.border}`,
    borderRadius: t.radius.lg,
    boxShadow: t.shadow.card
  },

  sectionCard: {
    background: t.color.surface,
    border: `1px solid ${t.color.border}`,
    borderRadius: t.radius.md,
    boxShadow: t.shadow.card,
    padding: t.spacing.lg
  },

  softCard: {
    background: t.color.surfaceAlt,
    border: `1px solid ${t.color.border}`,
    borderRadius: t.radius.md
  },

  metricCard: {
    background: t.color.surface,
    border: `1px solid ${t.color.border}`,
    borderRadius: 16,
    padding: 14,
    boxShadow: t.shadow.soft
  },

  infoCard: {
    background: t.color.infoBg,
    border: `1px solid ${t.color.infoBorder}`,
    borderRadius: t.radius.md,
    color: t.color.infoText
  },

  successCard: {
    background: t.color.successBg,
    border: `1px solid ${t.color.successBorder}`,
    borderRadius: t.radius.md,
    color: t.color.successText
  },

  warningCard: {
    background: t.color.warningBg,
    border: `1px solid ${t.color.warningBorder}`,
    borderRadius: t.radius.md,
    color: t.color.warningText
  },

  dangerCard: {
    background: t.color.dangerBg,
    border: `1px solid ${t.color.dangerBorder}`,
    borderRadius: t.radius.md,
    color: t.color.dangerText
  },

  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 12
  },

  sectionTitle: {
    fontSize: t.type.section.fontSize,
    fontWeight: t.type.section.fontWeight,
    lineHeight: t.type.section.lineHeight,
    color: t.color.text,
    letterSpacing: "-0.02em"
  },

  sectionSubtitle: {
    fontSize: 12,
    color: t.color.textMuted,
    lineHeight: 1.4,
    marginTop: 4
  },

  cardTitle: {
    fontSize: t.type.card.fontSize,
    fontWeight: t.type.card.fontWeight,
    lineHeight: t.type.card.lineHeight,
    color: t.color.text
  },

  bodyText: {
    fontSize: t.type.body.fontSize,
    lineHeight: t.type.body.lineHeight,
    color: t.color.textSoft
  },

  metaText: {
    fontSize: t.type.meta.fontSize,
    fontWeight: t.type.meta.fontWeight,
    lineHeight: t.type.meta.lineHeight,
    color: t.color.textMuted
  },

  label: {
    fontSize: 12,
    fontWeight: 800,
    color: t.color.textMuted,
    marginBottom: 6,
    display: "block"
  },

  input: {
    height: t.controlHeight.md,
    width: "100%",
    padding: "0 12px",
    borderRadius: t.radius.sm,
    border: `1px solid ${t.color.border}`,
    background: "#FFFFFF",
    color: t.color.text,
    fontSize: 13,
    outline: "none",
    transition: "all 0.18s ease"
  },

  select: {
    height: t.controlHeight.md,
    width: "100%",
    padding: "0 12px",
    borderRadius: t.radius.sm,
    border: `1px solid ${t.color.border}`,
    background: "#FFFFFF",
    color: t.color.text,
    fontSize: 13,
    outline: "none"
  },

  textarea: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: t.radius.sm,
    border: `1px solid ${t.color.border}`,
    background: "#FFFFFF",
    color: t.color.text,
    fontSize: 13,
    outline: "none",
    resize: "vertical"
  },

  primaryButton: {
    ...baseButton,
    border: "none",
    background: t.gradient.cta,
    color: "#FFFFFF",
    boxShadow: "0 10px 20px rgba(36, 87, 214, 0.18)"
  },

  secondaryButton: {
    ...baseButton,
    border: `1px solid ${t.color.border}`,
    background: "#FFFFFF",
    color: t.color.text
  },

  ghostButton: {
    ...baseButton,
    border: "none",
    background: "transparent",
    color: t.color.textSoft
  },

  dangerButton: {
    ...baseButton,
    border: `1px solid ${t.color.dangerBorder}`,
    background: t.color.dangerBg,
    color: t.color.dangerText
  },

  segmentedTab: {
    ...baseButton,
    minHeight: 38,
    padding: "0 14px",
    border: `1px solid ${t.color.border}`,
    background: "#FFFFFF",
    color: t.color.textSoft,
    borderRadius: t.radius.pill
  },

  segmentedTabActive: {
    ...baseButton,
    minHeight: 38,
    padding: "0 14px",
    border: `1px solid ${t.color.infoBorder}`,
    background: t.color.infoBg,
    color: t.color.infoText,
    borderRadius: t.radius.pill,
    boxShadow: "0 8px 18px rgba(36, 87, 214, 0.10)"
  },

  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    minHeight: 28,
    padding: "0 10px",
    borderRadius: t.radius.pill,
    border: `1px solid ${t.color.border}`,
    background: "#FFFFFF",
    color: t.color.textSoft,
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1
  },

  pillInfo: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    minHeight: 28,
    padding: "0 10px",
    borderRadius: t.radius.pill,
    border: `1px solid ${t.color.infoBorder}`,
    background: t.color.infoBg,
    color: t.color.infoText,
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1
  },

  pillSuccess: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    minHeight: 28,
    padding: "0 10px",
    borderRadius: t.radius.pill,
    border: `1px solid ${t.color.successBorder}`,
    background: t.color.successBg,
    color: t.color.successText,
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1
  },

  pillDanger: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    minHeight: 28,
    padding: "0 10px",
    borderRadius: t.radius.pill,
    border: `1px solid ${t.color.dangerBorder}`,
    background: t.color.dangerBg,
    color: t.color.dangerText,
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1
  },

  statCard: {
    background: "#FFFFFF",
    border: `1px solid ${t.color.border}`,
    borderRadius: 14,
    padding: 12
  },

  cardHover: {
    background: "#FFFFFF",
    border: `1px solid ${t.color.border}`,
    borderRadius: t.radius.md,
    boxShadow: t.shadow.card,
    transition: "all 0.18s ease"
  },

  cardSelected: {
    background: "#FFFFFF",
    border: `1px solid ${t.color.infoBorder}`,
    borderRadius: t.radius.md,
    boxShadow: "0 10px 24px rgba(36, 87, 214, 0.10)",
    transition: "all 0.18s ease"
  },

  emptyState: {
    minHeight: 220,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: t.color.textMuted,
    fontSize: 14,
    fontWeight: 700,
    borderRadius: t.radius.md,
    border: `1px dashed ${t.color.border}`,
    background: t.color.surfaceAlt,
    padding: 20
  },

  sidebarShell: {
    background: t.gradient.sidebar,
    color: "#FFFFFF",
    boxShadow: t.shadow.hero,
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    borderRight: "1px solid rgba(255,255,255,0.05)"
  },

  sidebarHeader: {
    padding: "18px 14px 16px",
    borderBottom: "1px solid rgba(255,255,255,0.06)"
  },

  sidebarBrand: {
    fontSize: 20,
    fontWeight: 900,
    lineHeight: 1,
    letterSpacing: "-0.03em",
    color: "#FFFFFF"
  },

  sidebarBrandMeta: {
    fontSize: 12,
    color: "rgba(226,232,240,0.70)",
    marginTop: 8,
    lineHeight: 1.45
  },

  sidebarSectionLabel: {
    fontSize: 10,
    fontWeight: 900,
    color: "rgba(255,255,255,0.42)",
    letterSpacing: "0.08em",
    textTransform: "uppercase"
  },

  sidebarItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minHeight: 44,
    padding: "0 12px",
    borderRadius: 14,
    color: "rgba(255,255,255,0.88)",
    fontSize: 13,
    fontWeight: 700,
    textDecoration: "none"
  },

  sidebarItemActive: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minHeight: 44,
    padding: "0 12px",
    borderRadius: 14,
    color: "#FFFFFF",
    fontSize: 13,
    fontWeight: 800,
    textDecoration: "none",
    background: "linear-gradient(135deg, rgba(59,130,246,0.92), rgba(99,102,241,0.92))",
    boxShadow: "0 12px 22px rgba(59,130,246,0.22)"
  },

  sidebarIconBadge: {
    width: 26,
    height: 26,
    borderRadius: 10,
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "#E2E8F0",
    display: "grid",
    placeItems: "center",
    fontSize: 10,
    fontWeight: 900,
    flexShrink: 0
  },

  sidebarIconBadgeActive: {
    width: 26,
    height: 26,
    borderRadius: 10,
    background: "rgba(255,255,255,0.18)",
    border: "1px solid rgba(255,255,255,0.20)",
    color: "#FFFFFF",
    display: "grid",
    placeItems: "center",
    fontSize: 10,
    fontWeight: 900,
    flexShrink: 0
  }
}